/**
 * Reservation Management Module - Orchestrator Hook
 *
 * Combines queries, mutations, and UI state for the reservation management view.
 */

import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastMessageContext } from '@context/toast-message-cotnext';
import {
  useTaskManagementDashboard,
  useTaskManagementStats,
  useReservationList,
  useReservationDetail,
  useAvailableProperties,
} from '../services/reservations.queries';
import {
  useLateCheckoutValidate,
  useLateCheckoutApply,
  useExtendStayValidate,
  useExtendStayApply,
  useConfirmCheckout,
  useMoveToProperty,
} from '../services/reservations.mutations';
import type {
  ReservationTab,
  ILateCheckoutRequest,
  IExtendStayRequest,
  IAvailablePropertiesParams,
  IMoveToPropertyRequest,
} from '../types';

export const useReservationManagement = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useContext(ToastMessageContext);
  const isRtl = i18n.language === 'ar';

  // --- Tab & Pagination State ---
  const [activeTab, setActiveTab] = useState<ReservationTab>('checkin_today');
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState('4');

  // --- Filter State ---
  const [searchFilter, setSearchFilter] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState<string | undefined>(undefined);
  const [propertyIdFilter, setPropertyIdFilter] = useState<number | undefined>(undefined);

  const handleTabChange = (tab: ReservationTab) => {
    setActiveTab(tab);
    setActivePage(1);
    setSearchFilter('');
    setBookingStatusFilter(undefined);
    setPropertyIdFilter(undefined);
  };

  // --- Selected Reservation ---
  const [selectedReservationId, setSelectedReservationId] = useState<number | undefined>(undefined);

  // --- Available Properties Params ---
  const [availablePropertiesParams, setAvailablePropertiesParams] = useState<IAvailablePropertiesParams | undefined>(undefined);

  // --- Queries ---
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    refetch: refetchDashboard,
  } = useTaskManagementDashboard();

  const {
    data: statsData,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useTaskManagementStats();

  const {
    data: reservationsData,
    isLoading: reservationsLoading,
    refetch: refetchReservations,
  } = useReservationList({
    tab: activeTab,
    pageNumber: activePage,
    pageSize: Number(perPage),
    search: searchFilter || undefined,
    bookingStatus: bookingStatusFilter,
    propertyId: propertyIdFilter,
  });

  const {
    data: reservationDetailData,
    isLoading: detailLoading,
  } = useReservationDetail(selectedReservationId);

  const {
    data: availablePropertiesData,
    isLoading: availablePropertiesLoading,
  } = useAvailableProperties(availablePropertiesParams);

  // --- Mutations ---
  const lateCheckoutValidateMutation = useLateCheckoutValidate();
  const lateCheckoutApplyMutation = useLateCheckoutApply();
  const extendStayValidateMutation = useExtendStayValidate();
  const extendStayApplyMutation = useExtendStayApply();
  const confirmCheckoutMutation = useConfirmCheckout();
  const moveToPropertyMutation = useMoveToProperty();

  // --- Actions ---
  const handleLateCheckoutValidate = async (payload: ILateCheckoutRequest) => {
    try {
      const response = await lateCheckoutValidateMutation.mutateAsync(payload);
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  const handleLateCheckoutApply = async (payload: ILateCheckoutRequest) => {
    try {
      const response = await lateCheckoutApplyMutation.mutateAsync(payload);
      if (response.isSuccess) {
        const msg = isRtl ? 'تم تطبيق المغادرة المتأخرة بنجاح' : 'Late checkout applied successfully';
        showSuccess(msg);
      } else {
        showError(response.errorMessages?.[0] || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  const handleExtendStayValidate = async (payload: IExtendStayRequest) => {
    try {
      const response = await extendStayValidateMutation.mutateAsync(payload);
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  const handleExtendStayApply = async (payload: IExtendStayRequest) => {
    try {
      const response = await extendStayApplyMutation.mutateAsync(payload);
      if (response.isSuccess) {
        const msg = isRtl ? 'تم تمديد الإقامة بنجاح' : 'Stay extended successfully';
        showSuccess(msg);
      } else {
        showError(response.errorMessages?.[0] || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  const handleConfirmCheckout = async (reservationId: number) => {
    try {
      const response = await confirmCheckoutMutation.mutateAsync({ reservationId });
      if (response?.isSuccess) {
        const msg = isRtl ? 'تم تأكيد تسجيل خروج الضيف بنجاح' : 'Guest checkout confirmed successfully';
        showSuccess(msg);
      } else {
        const errMsg = response?.errorMessages?.[0] as any;
        const msg = typeof errMsg === 'object' ? (isRtl ? errMsg?.messageAr : errMsg?.messageEn) : errMsg;
        showError(msg || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  const handleMoveToProperty = async (payload: IMoveToPropertyRequest) => {
    try {
      const response = await moveToPropertyMutation.mutateAsync(payload);
      if (response.isSuccess) {
        const msg = isRtl ? 'تم نقل الحجز بنجاح' : 'Reservation moved successfully';
        showSuccess(msg);
      } else {
        showError(response.errorMessages?.[0] || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
      return response;
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
      return null;
    }
  };

  return {
    // Tab & Pagination
    activeTab,
    setActiveTab,
    handleTabChange,
    activePage,
    setActivePage,
    perPage,
    setPerPage,
    isRtl,

    // Filters
    searchFilter,
    setSearchFilter,
    bookingStatusFilter,
    setBookingStatusFilter,
    propertyIdFilter,
    setPropertyIdFilter,

    // Dashboard
    dashboard: dashboardData?.result ?? null,
    dashboardLoading,
    refetchDashboard,

    // Stats
    stats: statsData?.result ?? null,
    statsLoading,
    refetchStats,

    // Reservation List
    reservations: reservationsData?.result?.items ?? [],
    totalRecords: reservationsData?.result?.totalCount ?? 0,
    reservationsLoading,
    refetchReservations,

    // Reservation Detail
    selectedReservationId,
    setSelectedReservationId,
    reservationDetail: reservationDetailData?.result ?? null,
    detailLoading,

    // Available Properties
    availablePropertiesParams,
    setAvailablePropertiesParams,
    availableProperties: availablePropertiesData?.result ?? [],
    availablePropertiesLoading,

    // Mutation Loading States
    isLateCheckoutValidating: lateCheckoutValidateMutation.isPending,
    isLateCheckoutApplying: lateCheckoutApplyMutation.isPending,
    isExtendStayValidating: extendStayValidateMutation.isPending,
    isExtendStayApplying: extendStayApplyMutation.isPending,
    isConfirmingCheckout: confirmCheckoutMutation.isPending,
    isMovingToProperty: moveToPropertyMutation.isPending,

    // Actions
    handleLateCheckoutValidate,
    handleLateCheckoutApply,
    handleExtendStayValidate,
    handleExtendStayApply,
    handleConfirmCheckout,
    handleMoveToProperty,
  };
};
